/**
 * @author: chenl
 * @date: 2025/01/07
 * @desc: 报告详情页中转页面
 */
import React, { useMemo, useEffect } from 'react';
import { history, useParams, useLocation, getLocale } from 'umi';
import aegisLog from '@/log/aegis';
import { getReportByIdApi } from '@intelligence/services/reportService';
import { getReportOriginalResourcePath, getReportResourcePath } from '@intelligence/utils/reports';
import { getLangOfReportDetails } from '@intelligence/v3/utils/reports';
import { getCurrentRoutePath } from '@intelligence/utils/commonUtils';

const ReportDetailsMiddlePage: React.FC = () => {
  const { systemId, resourceId } = useParams<{ systemId: string; resourceId: string }>();
  const { search } = useLocation();

  // 从URL查询参数获取可选参数
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(search);
    return {
      referer: params.get('referer'),
      lang: params.get('lang'),
    };
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    const fetchReportAndRedirect = async () => {
      if (!systemId || !resourceId) {
        history.replace('/intelligence');
        return;
      }

      try {
        const res = await getReportByIdApi({ resource_ids: [resourceId] });
        if (cancelled) return;

        const reportDetails = res?.resource_list?.[0];

        // 从URL查询参数获取可选参数
        const referer = queryParams.referer || getCurrentRoutePath();

        const locale = getLocale();
        const reportLang = getLangOfReportDetails(
          locale,
          queryParams.lang || 'all',
        );
        const params = {
          id: reportDetails?.id || '',
          systemId,
          resourceId: reportDetails?.resource_id || resourceId,
          resourceName: (reportLang === 'cn'
            ? reportDetails?.title || reportDetails?.title_en
            : reportDetails?.title_en || reportDetails?.title) || '',
          resourcePath: getReportResourcePath(locale, {
            lang: reportLang,
            pdf: reportDetails?.pdf,
            pdf_en: reportDetails?.pdf_en,
          }),
          resourceOriginalPath: getReportOriginalResourcePath(locale, {
            lang: reportLang,
            original_file_name: reportDetails?.original_file_name,
          }),
          resourceLike: reportDetails?.report_like ?? 0,
          downloadLogKey: '',
          lang: reportLang,
          referer,
        };

        const urlSearchParams = new URLSearchParams();
        Object.entries(params).forEach(([param, value]) => {
          urlSearchParams.set(param, encodeURIComponent(value));
        });

        history.push(`/intelligence/pdfPreviewDownloadLikeShare?${urlSearchParams.toString()}`);
      } catch (error: any) {
        if (cancelled) return;
        console.error('Failed to fetch report details:', error);
        aegisLog.reportEvent({
          name: 'report_details_middle_page_error',
          msg: `report details middle page error: ${error?.msg}`,
          // ext1: '',
          // ext2: '',
          // ext3: '',
        });
        history.replace('/intelligence');
      }
    };

    fetchReportAndRedirect();
    return () => { cancelled = true; };
  }, [systemId, resourceId, queryParams]);

  return <div className="report-details-middle-page"></div>;
};

export default ReportDetailsMiddlePage;
